// Dropped-email parsing — format detection, the normalised shape, and the helpers both parsers feed.
//
// ⛔ ONE SHAPE, TWO FORMATS. Outlook hands the browser a .msg on some platforms and a .eml on others,
// and which is which is a platform fact we do not control. Everything downstream — the task
// extractor, the attachment upload, the review panel — sees ONLY the normalised shape below and
// never learns which format it came from. Adding a third format later is a new adapter and nothing
// else.
//
// ⛔ DETECT BY MAGIC BYTES, NEVER BY EXTENSION. A drag can arrive named .msg and be MIME, or carry no
// extension at all. The first eight bytes are the fact; the filename is a hint.
//
// ⛔ PARSE IN THE BROWSER. The bytes never go through a Vercel function — same reason
// src/utils/uploadFile.js PUTs straight to R2: the 4.5 MB function body cap would reject a normal
// email with one photo in it. Attachments go to R2 by presigned PUT afterwards, unchanged.
//
// ⛔ FAIL LOUDLY, NEVER PARTIALLY. Every failure path here throws an EmailParseError with a message
// written for the person who dropped the file. A half-read email that looks complete is the exact
// failure this codebase has spent three weeks removing.

export class EmailParseError extends Error {
  constructor(message, { code = 'parse_failed', detail = null } = {}) {
    super(message);
    this.name = 'EmailParseError';
    this.code = code;
    this.detail = detail;
  }
}

// ═══ FORMAT DETECTION ═══════════════════════════════════════════════════════════════════════════
export const FORMAT = { MSG: 'msg', EML: 'eml', UNKNOWN: 'unknown' };

// Compound File Binary — the container Outlook classic writes. The full 8-byte signature, not the
// first four: D0 CF 11 E0 alone also matches older Office formats we would not want to hand a .msg
// parser.
const CFB_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
// A .eml opens with RFC-822 headers in ASCII. Anchored at the start and case-insensitive, because
// header case is not guaranteed and a match mid-file means nothing.
const EML_FIRST_HEADER = /^(Received|Return-Path|From|To|Cc|Subject|Date|Message-ID|MIME-Version|Content-Type|Delivered-To|Sender|X-[A-Za-z0-9-]+)\s*:/i;

/**
 * Identify a dropped file from its first bytes.
 * @param {Uint8Array} head at least 8 bytes from offset 0
 * @returns {{ format: string, why: string }}
 */
export function detectFormat(head) {
  const b = head || new Uint8Array(0);
  if (b.length >= 8 && CFB_MAGIC.every((v, i) => b[i] === v)) {
    return { format: FORMAT.MSG, why: 'Compound File Binary signature (D0 CF 11 E0 A1 B1 1A E1)' };
  }
  // ⛔ Skip a UTF-8 BOM before testing for headers — a BOM'd .eml is still a .eml, and testing the
  // raw first byte would silently classify it UNKNOWN.
  let start = 0;
  if (b.length >= 3 && b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf) start = 3;
  const ascii = String.fromCharCode(...b.slice(start, Math.min(b.length, start + 128)));
  if (EML_FIRST_HEADER.test(ascii)) {
    return { format: FORMAT.EML, why: `RFC-822 header (${ascii.slice(0, ascii.indexOf(':') + 1)})` };
  }
  if (b.length >= 4 && b[0] === 0x50 && b[1] === 0x4b && b[2] === 0x03 && b[3] === 0x04) {
    return { format: FORMAT.UNKNOWN, why: 'ZIP container — a .zip or an Office document, not an email' };
  }
  return { format: FORMAT.UNKNOWN, why: 'no recognised email signature in the first bytes' };
}

/** Read enough of a File to identify it, without loading the whole thing. */
export async function sniff(file) {
  if (!file) throw new EmailParseError('No file was dropped.', { code: 'no_file' });
  const head = new Uint8Array(await file.slice(0, 128).arrayBuffer());
  const { format, why } = detectFormat(head);
  return { format, why, name: file.name || null, size: file.size ?? null, head };
}

// ═══ THE NORMALISED SHAPE ═══════════════════════════════════════════════════════════════════════
//
//   {
//     format:      'msg' | 'eml'          which adapter produced this — provenance, not behaviour
//     subject:     string | null
//     from:        { name: string|null, email: string|null } | null
//     to:          [{ name, email }]      always an array, possibly empty
//     cc:          [{ name, email }]
//     sentAt:      'YYYY-MM-DDTHH:mm:ssZ' | null
//     textBody:    string | null
//     htmlBody:    string | null          NULL when the format did not carry usable HTML
//     attachments: [{
//        filename, mimeType, bytes: Uint8Array, size,
//        contentId:   string|null         the cid: the body references, when there is one
//        disposition: 'inline'|'attachment'|null
//     }]
//   }
//
// ⛔ NULL, NEVER ''. A missing sender is ABSENT, not an empty string — the standing rule, and it
// matters here because `source_email_from` renders only when truthy (TaskEngine.js:3736). An empty
// string would render an empty "From:" line, which reads as a bug rather than as missing data.
// normaliseAddress and blankToNull below are the only places a value becomes null; do not scatter
// `|| ''` through the adapters.

export const blankToNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

export function normaliseAddress(a) {
  if (!a) return null;
  const name = blankToNull(a.name ?? a.displayName ?? null);
  const email = blankToNull(a.email ?? a.address ?? a.smtpAddress ?? null);
  if (!name && !email) return null;         // an address with neither part is not an address
  return { name, email };
}

export const normaliseAddressList = (list) =>
  (Array.isArray(list) ? list : list ? [list] : []).map(normaliseAddress).filter(Boolean);

/**
 * ⛔ THE CONTRACT CHECK. Every adapter's output goes through this before anything downstream sees
 * it, so a parser that half-worked cannot present as a complete email. It throws rather than
 * repairing: a body we could not read is not an empty body.
 */
export function assertNormalised(msg) {
  if (!msg || typeof msg !== 'object') throw new EmailParseError('The parser returned nothing.', { code: 'empty_result' });
  const problems = [];
  if (!Object.values(FORMAT).includes(msg.format)) problems.push('format');
  if (!Array.isArray(msg.to)) problems.push('to must be an array');
  if (!Array.isArray(msg.cc)) problems.push('cc must be an array');
  if (!Array.isArray(msg.attachments)) problems.push('attachments must be an array');
  ['subject', 'textBody', 'htmlBody', 'sentAt'].forEach((k) => {
    if (msg[k] === '') problems.push(`${k} is an empty string — absent must be null`);
  });
  if (problems.length) {
    throw new EmailParseError(
      'The email was read but came back in an unusable shape, so nothing was imported.',
      { code: 'bad_shape', detail: problems.join('; ') }
    );
  }
  // ⛔ Neither body is a HARD failure. There is nothing to extract tasks from and nothing to show,
  // and importing an email with no body would create tasks with no provenance.
  if (!msg.textBody && !msg.htmlBody) {
    throw new EmailParseError(
      'This email has no readable body — nothing was imported. It may be encrypted, digitally '
      + 'protected (IRM/S-MIME), or stored in a format we cannot read. Forward it to yourself as a '
      + 'plain message and try again, or paste the text instead.',
      { code: 'no_body' }
    );
  }
  return msg;
}

/**
 * The best plain text this message can offer, for the task extractor — which reads text, not HTML.
 * ⛔ Prefers the real text/plain part; falls back to stripping the HTML only when there is no text
 * part at all. Returns NULL when there is nothing, never '' — the caller must be able to tell "no
 * body" from "an empty body", and assertNormalised has already refused the genuinely bodyless case.
 * The strip is deliberately crude: it exists to feed an extractor, never to render.
 */
export function plainTextFrom(msg) {
  if (!msg) return null;
  if (blankToNull(msg.textBody)) return msg.textBody;
  const html = blankToNull(msg.htmlBody);
  if (!html) return null;
  const text = html
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#39;/gi, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return blankToNull(text);
}

// ═══ CLIPBOARD ═══════════════════════════════════════════════════════════════════════════════════
//
// ⛔ THIS IS THE ORIGINAL COMPLAINT. "The images don't come through when we paste" — and there was
// no onPaste handler in the app at all. Pasting went into a plain <textarea>, which by definition
// receives text/plain and nothing else. Every image was being discarded by the browser before any
// of our code ran.
//
// ⛔ AND THE CLIPBOARD MAY NOT CARRY THEM EITHER. When Outlook on Windows copies a message BODY, it
// writes CF_HTML referencing images as LOCAL TEMP PATHS —
//     <img src="file:///C:/Users/…/AppData/Local/Temp/msohtmlclip1/01/clip_image001.png">
// A browser cannot read file:// and never will. So for a whole-body copy the bytes are genuinely
// absent from the clipboard, and no handler can conjure them. A single copied image, a screenshot,
// or a data: URI IS readable and does come through.
//
// So this reports BOTH halves: what it captured, and what it could see referenced but not read,
// with the reason. An image silently missing from a pasted body is the failure that started this;
// an image counted as unreadable is a fact the user can act on.

const IMG_SRC = /<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi;

/** Decode a data: URI to bytes, or null when it is not one we can read. */
export function dataUriToBytes(uri) {
  const m = /^data:([^;,]+)?(;base64)?,(.*)$/is.exec(String(uri || ''));
  if (!m) return null;
  const [, mime, b64, payload] = m;
  try {
    let bytes;
    if (b64) {
      const bin = atob(payload);
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(payload));
    }
    // ⛔ ZERO BYTES IS NOT AN IMAGE, it is a failed decode. A browser's atob() throws on malformed
    // base64 and this would be caught below, but Node's is lenient and returns nothing — so the
    // length check, not the throw, is what makes this honest on both. Absence must not arrive
    // dressed as a zero-length attachment.
    return bytes.length ? { bytes, mimeType: mime || (b64 ? 'application/octet-stream' : 'text/plain') } : null;
  } catch { return null; }
}

/**
 * Everything a paste actually carried.
 * ⛔ MUST be called SYNCHRONOUSLY from the paste event — clipboardData is emptied when the handler
 * yields, exactly like dataTransfer. Hence the two-stage shape: grab the File handles here, read
 * their bytes afterwards with readClipboardImages().
 *
 * @returns { text, html, types, imageFiles: File[], unreadable: [{src, why}] }
 */
export function readClipboard(clipboardData) {
  const cd = clipboardData;
  if (!cd) return { text: null, html: null, types: [], imageFiles: [], unreadable: [] };
  const types = Array.from(cd.types || []);
  const text = blankToNull(cd.getData('text/plain'));
  const html = blankToNull(cd.getData('text/html'));

  // Real bytes on the clipboard: a copied image, a screenshot, some Mac paths.
  const imageFiles = Array.from(cd.items || [])
    .filter((it) => it.kind === 'file' && /^image\//i.test(it.type || ''))
    .map((it) => it.getAsFile())
    .filter(Boolean);

  // What the HTML points at that we will NOT be able to fetch. Counted so the user is told rather
  // than left with a body whose pictures quietly vanished.
  const unreadable = [];
  if (html) {
    let m;
    IMG_SRC.lastIndex = 0;
    while ((m = IMG_SRC.exec(html)) !== null) {
      const src = m[1];
      if (/^data:/i.test(src)) continue;                              // readable — handled separately
      if (/^https?:/i.test(src)) continue;                            // remote; the browser can load it
      if (/^file:/i.test(src)) unreadable.push({ src, why: 'your email program left it as a file on this PC, which a browser cannot open' });
      else if (/^cid:/i.test(src)) unreadable.push({ src, why: 'it is an embedded email image, which only arrives with the saved email file' });
      else unreadable.push({ src, why: 'the reference is not something a browser can fetch' });
    }
  }
  return { text, html, types, imageFiles, unreadable };
}

/**
 * Turn the clipboard's readable images into normalised attachments: real image files plus any
 * data: URIs inlined in the HTML.
 * ⛔ Marked 'inline' — they came out of a message body, so they are body content by definition, and
 * looksDecorative decides only whether the UI folds them.
 */
export async function readClipboardImages({ imageFiles, html }) {
  const out = [];
  for (const f of imageFiles || []) {
    const bytes = new Uint8Array(await f.arrayBuffer());
    out.push({
      filename: blankToNull(f.name), mimeType: f.type || 'image/png',
      bytes, size: bytes.length, contentId: null, disposition: 'inline',
    });
  }
  if (html) {
    let m;
    IMG_SRC.lastIndex = 0;
    while ((m = IMG_SRC.exec(html)) !== null) {
      if (!/^data:/i.test(m[1])) continue;
      const d = dataUriToBytes(m[1]);
      if (!d || !d.bytes.length) continue;
      out.push({
        filename: null, mimeType: d.mimeType, bytes: d.bytes, size: d.bytes.length,
        contentId: null, disposition: 'inline',
      });
    }
  }
  return out;
}

/** "Name <email>" | "Name" | "email" | null — for source_email_from, which renders only when truthy. */
export function formatSender(from) {
  if (!from) return null;
  if (from.name && from.email) return `${from.name} <${from.email}>`;
  return from.name || from.email || null;
}

// ═══ IMAGE ROLE — for DISPLAY, never for deciding what to keep ══════════════════════════════════
//
// ⛔ NOTHING IS DROPPED BY THESE HEURISTICS. Both .msg and .eml separate INLINE from ATTACHMENT
// reliably, and neither separates a signature logo from a screenshot someone pasted into the body:
// both are inline, both cid-referenced, both positioned in the body flow. A size threshold would
// delete a cropped notice or a spreadsheet snippet — precisely the content worth keeping. So role
// and dimensions drive a FOLD in the UI ("3 signature images"), and the only thing that ever skips
// an image is the hard runaway cap, which is counted and named.

// ⛔ A DEGENERATE FRAME IS UNMEASURABLE, NOT ZERO-SIZED. A corrupt or truncated header can parse to
// 0x0, and 0 <= DECORATION_MAX_EDGE, so a broken image would silently qualify as decoration and get
// folded away. Same absence-treated-as-an-answer shape as everything else: unknown must read as
// unknown. Caught by a malformed-JPEG fixture, not by inspection.
const positive = (width, height) => (width > 0 && height > 0 ? { width, height } : null);

/** Pixel dimensions from the header bytes alone — no decoding. null when not an image we can read. */
export function imageDimensions(bytes, mimeType) {
  const b = bytes;
  if (!b || b.length < 24) return null;
  // PNG: 8-byte signature, then IHDR with width/height as big-endian uint32 at 16 and 20.
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
    return positive(dv.getUint32(16), dv.getUint32(20));
  }
  // GIF: 'GIF8', then width/height as little-endian uint16 at 6 and 8.
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) {
    const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
    return positive(dv.getUint16(6, true), dv.getUint16(8, true));
  }
  // JPEG: walk the segment chain to the first SOF marker. Bounded — a corrupt file must not spin.
  if (b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i += 1; continue; }
      const marker = b[i + 1];
      // SOF0..SOF15 except the non-frame markers DHT (C4), JPG (C8) and DAC (CC).
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
        return positive(dv.getUint16(i + 7), dv.getUint16(i + 5));
      }
      const len = (b[i + 2] << 8) | b[i + 3];
      if (len < 2) return null;                 // malformed segment length — stop rather than loop
      i += 2 + len;
    }
    return null;
  }
  if (mimeType && /^image\//i.test(mimeType)) return null;   // an image we cannot measure
  return null;
}

export const DECORATION_MAX_EDGE = 200;   // px — a signature logo or icon is small in BOTH directions
export const DECORATION_MAX_BYTES = 25 * 1024;

/**
 * Is this image PROBABLY decoration (a logo, an icon, a signature graphic) rather than content?
 * ⛔ Used ONLY to collapse it behind a fold. A false positive costs one click; it never deletes.
 * Deliberately conservative: an image has to be small on BOTH axes AND small in bytes to qualify,
 * so a wide-but-short cropped screenshot stays out in the open.
 */
export function looksDecorative(att) {
  if (!att || att.disposition !== 'inline') return false;
  if (!att.mimeType || !/^image\//i.test(att.mimeType)) return false;
  if ((att.size ?? 0) > DECORATION_MAX_BYTES) return false;
  const d = imageDimensions(att.bytes, att.mimeType);
  if (!d) return false;                       // unmeasurable → treat as content, never as noise
  return d.width <= DECORATION_MAX_EDGE && d.height <= DECORATION_MAX_EDGE;
}

// ═══ DEDUPLICATION — lossless, and the real fix for the forwarded thread ════════════════════════
//
// ⛔ THIS IS WHY A THREAD WITH "FIFTY IMAGES" IS NOT FIFTY IMAGES. One signature logo repeated once
// per message down a chain is the SAME BYTES fifty times. Collapsing identical content is not a
// judgement call and loses nothing, unlike a size rule. A pasted screenshot appears once and is
// never touched.

export async function sha256Hex(bytes) {
  const buf = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Group attachments by content hash. Returns unique attachments, each carrying every cid that
 * referred to it, so rewriting the body later can point all of them at the one stored object.
 * @returns {{ unique: Array, duplicatesRemoved: number }}
 */
export async function dedupeAttachments(attachments) {
  const byHash = new Map();
  let duplicatesRemoved = 0;
  for (const att of attachments || []) {
    const hash = await sha256Hex(att.bytes);
    const seen = byHash.get(hash);
    if (seen) {
      duplicatesRemoved += 1;
      if (att.contentId && !seen.contentIds.includes(att.contentId)) seen.contentIds.push(att.contentId);
      continue;
    }
    byHash.set(hash, { ...att, hash, contentIds: att.contentId ? [att.contentId] : [] });
  }
  return { unique: [...byHash.values()], duplicatesRemoved };
}

// ⛔ THE ONLY THING THAT EVER SKIPS AN IMAGE. A runaway guard, not a quality filter — and hitting it
// refuses the import rather than importing a subset, because a task built from part of an email is
// the failure mode we are here to prevent.
export const MAX_ATTACHMENTS = 50;

/**
 * A plain-English account of what came out, for the import panel. Counts EVERYTHING — the standing
 * rule is that absence is visible, so a skipped or collapsed item appears as a number, never as
 * silence.
 */
export function importSummary({ unique, duplicatesRemoved }) {
  const inline = unique.filter((a) => a.disposition === 'inline');
  const files = unique.filter((a) => a.disposition !== 'inline');
  const decorative = inline.filter(looksDecorative);
  const parts = [];
  parts.push(`${unique.length} item${unique.length === 1 ? '' : 's'} imported`);
  if (duplicatesRemoved) parts.push(`${duplicatesRemoved} duplicate${duplicatesRemoved === 1 ? '' : 's'} collapsed`);
  if (inline.length) parts.push(`${inline.length} inline image${inline.length === 1 ? '' : 's'}`);
  if (decorative.length) parts.push(`${decorative.length} of them look like signature graphics`);
  if (files.length) parts.push(`${files.length} attached file${files.length === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// ═══ THE ADAPTERS ═══════════════════════════════════════════════════════════════════════════════
//
// Two libraries, one shape. Each adapter's ONLY job is to map its library's output onto the
// normalised shape and hand it to assertNormalised — no business logic, no defaulting, no `|| ''`.
//
//   .msg → @kenjiuno/msgreader  (Apache-2.0)
//   .eml → postal-mime          (MIT-0, zero dependencies)
//
// ⛔ LOADED ON DEMAND, and the measurement is why. Imported statically they added 669 KB to the
// main bundle — 2.22 MB to 2.89 MB, a 30% increase on EVERY page load for every user, to support a
// feature used on one card of one screen. Most of it is iconv-lite's character-encoding tables,
// pulled in by the .msg reader. An earlier version of this comment argued for static imports on
// correctness grounds and said to revisit "if the bundle actually hurts". It hurts.
//
// The cost of loading late: if a deploy lands while someone has the page open, the chunk URL they
// would fetch on first drop can 404. That surfaces as the ordinary "couldn't read that file" error
// and a refresh fixes it — an acceptable trade against 669 KB, but a real one, and the reason the
// import sits inside a try that reports rather than throwing raw.
const toBytes = (v) => {
  if (!v) return new Uint8Array(0);
  if (v instanceof Uint8Array) return v;
  if (v instanceof ArrayBuffer) return new Uint8Array(v);
  if (ArrayBuffer.isView(v)) return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  if (typeof v === 'string') return new TextEncoder().encode(v);
  return new Uint8Array(0);
};

// A cid can arrive wrapped in angle brackets ('<image001@01D9.png>') or bare, depending on the
// format and the sending client. The body always references it bare, so strip them once here rather
// than at every comparison site.
const cleanCid = (v) => blankToNull(String(v ?? '').replace(/^<|>$/g, ''));

// ⛔ ISO 8601 or null — never a Date object and never a locale string. Downstream writes this to a
// timestamp column and renders it; one canonical wire format keeps both honest.
function toIso(v) {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ── .msg ────────────────────────────────────────────────────────────────────────────────────────
//
// ⛔ THE MAPPING IS EXPORTED AND PURE so it can be tested without a real Outlook file. Everything
// below the library boundary is ours; the boundary itself (does msgreader read a genuine .msg
// correctly?) can only be proved with a message that came out of Outlook, and that is what the
// drag test supplies. Splitting them means a bug in OUR mapping never hides behind "we have no
// sample yet".
//
// @param data     msgreader's getFileData() output
// @param getBytes (attachmentField) => Uint8Array — reader.getAttachment(a).content, injected
export function mapMsgFields(data, getBytes) {
  // ⛔ TWO HTML PROPERTIES, NOT ONE. msgreader maps PidTagBodyHtml's UNICODE form (1013001F) to
  // `bodyHtml` and its BINARY form (10130102) to `html` — lib/const.js:113-114. Reading only
  // `bodyHtml` silently loses the HTML body of every message that used the binary encoding.
  // Verified against the library's own property table, not assumed.
  //
  // ⛔ AND NEITHER MAY EXIST. PidTagRtfCompressed (0x1009) is mapped to NO field: this library never
  // decodes compressed RTF, and its @kenjiuno/decompressrtf dependency is declared in package.json
  // but referenced nowhere in lib/. So a .msg whose rich body lives only in compressed RTF yields
  // NO html — textBody still works, htmlBody is an honest null. That single property is the whole
  // of the Phase 3 risk for .msg.
  const htmlBody = blankToNull(data.bodyHtml)
    ?? blankToNull(typeof data.html === 'string'
      ? data.html
      : data.html ? new TextDecoder('utf-8').decode(toBytes(data.html)) : null);

  const recips = Array.isArray(data.recipients) ? data.recipients : [];
  const pick = (t) => normaliseAddressList(
    recips.filter((r) => (r.recipType || 'to') === t)
      .map((r) => ({ name: r.name, email: r.smtpAddress || r.email }))
  );

  const attachments = [];
  for (const a of data.attachments || []) {
    if (a.innerMsgContent) continue;      // a forwarded .msg inside a .msg has no file bytes
    let bytes;
    try { bytes = toBytes(getBytes(a)); }
    catch (e) {
      throw new EmailParseError(
        `Attachment "${a.fileName || '(unnamed)'}" could not be read, so nothing was imported.`,
        { code: 'attachment_unreadable', detail: e.message }
      );
    }
    const contentId = cleanCid(a.pidContentId);
    attachments.push({
      filename: blankToNull(a.fileName) || blankToNull(a.name),
      mimeType: blankToNull(a.attachMimeTag) || 'application/octet-stream',
      bytes,
      size: bytes.length,
      contentId,
      // ⛔ INLINE IS DERIVED, and this is as far as the format goes. A cid means the body refers to
      // it; attachmentHidden means Outlook keeps it out of the attachment list. Either is inline.
      // Neither distinguishes a signature logo from a pasted screenshot — see looksDecorative.
      disposition: (contentId || a.attachmentHidden) ? 'inline' : 'attachment',
    });
  }

  return {
    subject: blankToNull(data.subject),
    from: normaliseAddress({ name: data.senderName, email: data.senderSmtpAddress || data.senderEmail }),
    to: pick('to'),
    cc: pick('cc'),
    // clientSubmitTime is when the sender hit send; messageDeliveryTime is when it arrived.
    sentAt: toIso(data.clientSubmitTime) ?? toIso(data.messageDeliveryTime) ?? toIso(data.creationTime),
    textBody: blankToNull(data.body),
    htmlBody,
    attachments,
  };
}

async function parseMsg(file) {
  try {
    const { default: MsgReader } = await import('@kenjiuno/msgreader');
    const reader = new MsgReader(await file.arrayBuffer());
    const data = reader.getFileData();
    if (data && data.error) throw new Error(data.error);
    return mapMsgFields(data, (a) => reader.getAttachment(a).content);
  } catch (e) {
    if (e instanceof EmailParseError) throw e;
    throw new EmailParseError(
      'This Outlook message could not be read, so nothing was imported. It may be encrypted or '
      + 'rights-protected. Try forwarding it to yourself as a plain message, or paste the text.',
      { code: 'msg_parse_failed', detail: e.message }
    );
  }
}

// ── .eml ────────────────────────────────────────────────────────────────────────────────────────
async function parseEml(file) {
  let mail;
  try {
    const { default: PostalMime } = await import('postal-mime');
    mail = await PostalMime.parse(await file.arrayBuffer());
  } catch (e) {
    throw new EmailParseError(
      'This email file could not be read, so nothing was imported. It may be encrypted or damaged.',
      { code: 'eml_parse_failed', detail: e.message }
    );
  }
  const attachments = (mail.attachments || []).map((a) => {
    const bytes = toBytes(a.content);
    const contentId = cleanCid(a.contentId);
    return {
      filename: blankToNull(a.filename),
      mimeType: blankToNull(a.mimeType) || 'application/octet-stream',
      bytes,
      size: bytes.length,
      contentId,
      // MIME states disposition outright. When it is absent, a Content-ID means the body points at
      // it, which is the same evidence .msg gives us.
      disposition: a.disposition === 'inline' || a.disposition === 'attachment'
        ? a.disposition
        : (contentId ? 'inline' : 'attachment'),
    };
  });
  return {
    subject: blankToNull(mail.subject),
    from: normaliseAddress(mail.from),
    to: normaliseAddressList(mail.to),
    cc: normaliseAddressList(mail.cc),
    sentAt: toIso(mail.date),
    textBody: blankToNull(mail.text),
    htmlBody: blankToNull(mail.html),
    attachments,
  };
}

/**
 * THE ENTRY POINT. A dropped File in, one normalised email out, or a throw that says why in words
 * a person can act on.
 */
export async function parseDroppedEmail(file) {
  const { format, why, name, size } = await sniff(file);
  if (format === FORMAT.UNKNOWN) {
    throw new EmailParseError(
      `"${name || 'That file'}" is not an email we can read — ${why}. Drag a message straight out of `
      + 'Outlook, or paste the text into the box instead.',
      { code: 'unrecognised_format', detail: why }
    );
  }
  const msg = format === FORMAT.MSG ? await parseMsg(file) : await parseEml(file);
  return assertNormalised({ ...msg, format, sourceName: name, sourceSize: size });
}
